"use client";

import { useCallback, useMemo } from "react";
import { useQueryAuthentications } from "@zapier/client";
import {
  useNamespacedTranslations,
  useRegisterFilter,
  useMultiValueParameter,
  type FilterControllerProps,
  type ActiveChip,
} from "@zapier/shared";
import { ConnectionFilterMenu } from "./ConnectionFilterMenu";

const ICON = "actionLink";

export function ConnectionFilterController({
  isOpen,
  namespace,
  onClose: _onClose,
  onInteraction,
}: FilterControllerProps): JSX.Element | null {
  const t = useNamespacedTranslations(namespace);
  const connections = useMultiValueParameter(
    "connections",
    "connectionsOperator",
  );

  const hasValue = connections.values.length > 0;
  // Zaps Connection filter stores authentication PKs as decimal strings.
  // Skip non-numeric URL values so `connections=badstring` does not 400.
  const selectedNumericIds = connections.values.filter((id) =>
    /^\d+$/.test(id),
  );

  const { authentications: selectedAuths } = useQueryAuthentications(
    {
      ids: selectedNumericIds,
      limit: Math.max(selectedNumericIds.length, 1),
    },
    { enabled: selectedNumericIds.length > 0 },
  );

  const connectionNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const auth of selectedAuths) {
      if (auth.id !== undefined && auth.title) {
        map.set(String(auth.id), auth.title);
      }
    }
    return map;
  }, [selectedAuths]);

  const resolveConnectionName = useCallback(
    (id: string): string => connectionNameById.get(id) ?? id,
    [connectionNameById],
  );

  let label = "";
  let ariaLabel = "";
  let tooltip: string | undefined;
  const first = hasValue
    ? resolveConnectionName(connections.values[0])
    : undefined;
  const isSingle = connections.values.length === 1;
  const isMultiple = connections.values.length > 1;
  if (isSingle && first !== undefined) {
    label = t("connection.chipLabel.single", { first });
    ariaLabel = t("connection.chipAriaLabel.single", { first });
  } else if (isMultiple && first !== undefined) {
    const count = connections.values.length - 1;
    label = t("connection.chipLabel.multiple", { first, count });
    ariaLabel = t("connection.chipAriaLabel.multiple", { first, count });
    tooltip = connections.values
      .map((id) => resolveConnectionName(id))
      .join(", ");
  }

  let chip: ActiveChip | undefined;
  // Include label in the signature so the chip re-renders when an async
  // name lookup resolves id -> display name.
  let signature = "connection:none";
  if (hasValue) {
    chip = {
      key: "connection",
      iconName: ICON,
      title: t("connection.label"),
      label,
      ariaLabel,
      tooltip,
    };
    signature = `connection:${connections.values.join(",")}:${label}`;
  } else if (isOpen) {
    chip = {
      key: "connection",
      iconName: ICON,
      title: t("connection.label"),
      ariaLabel: t("connection.label"),
      isDraft: true,
    };
    signature = "connection:draft";
  }

  useRegisterFilter({
    type: "connection",
    hasValue,
    signature,
    chip,
    // ConnectionFilterMenu calls useTranslations("zap-filter-bar") internally;
    // do not pass a namespace prop (it's Zap-specific and stays that way).
    renderMenu: () => (
      <ConnectionFilterMenu
        selectedIds={connections.values}
        onToggle={(id) => {
          connections.toggle(id);
          onInteraction?.();
        }}
      />
    ),
  });
  // eslint-disable-next-line unicorn/no-null -- headless controller renders nothing
  return null;
}
